/**
 * UranIA PA — Auth store (Zustand) — US-009.
 *
 * Lo store custodisce in memoria il solo `accessToken` e i claim minimi
 * derivati dall'OIDC `User` (subject, tenant_id, acr, roles).
 *
 * Boundary di sicurezza (FR-F1-10, analisi §18 e §16.4):
 *   - NIENTE `refresh_token` qui (niente esposizione XSS): il refresh
 *     avviene via silent renew Keycloak (SSO cookie HttpOnly).
 *   - NIENTE `id_token` qui in chiaro (passato ad oidc-client-ts solo per il
 *     post-logout redirect; non serve in cache).
 *   - Lo store è in-memory (devtools Zustand abilitati solo in dev).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface UraniaPrincipal {
  readonly subject: string;
  readonly tenantId: string | null;
  readonly acr: string | null;
  readonly roles: readonly string[];
  readonly preferredUsername: string | null;
}

export interface AuthState {
  readonly accessToken: string | null;
  readonly principal: UraniaPrincipal | null;
  readonly status: 'unauthenticated' | 'authenticated' | 'renewing';
  readonly setSession: (params: { accessToken: string; principal: UraniaPrincipal }) => void;
  readonly clearSession: () => void;
  readonly setRenewing: () => void;
}

const initialState = {
  accessToken: null,
  principal: null,
  status: 'unauthenticated' as const,
};

// Dev: persist su sessionStorage (per-tab, sopravvive a F5/navigate full-page).
// In prod prod si tornera' a in-memory puro + silent renew Keycloak SSO cookie
// HttpOnly (FR-F1-10 analisi §16.4). sessionStorage e' scelto rispetto a
// localStorage per minimizzare l'esposizione (per-tab, niente persistenza
// cross-tab/cross-session).
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,
      setSession: ({ accessToken, principal }): void => {
        set({ accessToken, principal, status: 'authenticated' });
      },
      clearSession: (): void => {
        set({ ...initialState });
      },
      setRenewing: (): void => {
        set({ status: 'renewing' });
      },
    }),
    {
      name: 'urania-auth-store',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        principal: state.principal,
        status: state.status,
      }),
    },
  ),
);

export const selectAccessToken = (state: AuthState): string | null => state.accessToken;
export const selectIsAuthenticated = (state: AuthState): boolean =>
  state.status === 'authenticated' && state.accessToken !== null;
export const selectPrincipal = (state: AuthState): UraniaPrincipal | null => state.principal;
